import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';
import { styled } from '@nykaa/ui-components';
import { pushEvent } from '@nykaa/data-layer';
import { HAMBURGER_CLICK } from 'shared/helpers/impressions/constants';
import { State } from 'shared/store/types';
import BackNav from 'shared/components/BackIcon';
import HamburgerIcon from 'components/Icons/Hamburger';
import { BODY_OVERFLOW_FOR_MODAL } from 'constants/dom';
import { useIsConfigHeader, useIsThemeHeader } from 'hooks/header';
import PageTypes from 'shared/constants/pageTypes';
import { getEligibleForVariant } from 'shared/helpers/experimentV2';
import { useIsAddressBarEnabled } from 'hooks/header';
import { AB_BOTTOM_NAV, AB_PDP_HAMBURGER } from 'shared/constants/experiments';
import {
  getFillColor,
  getInteractionLocation,
  hasUTMParams,
} from 'shared/components/Header/helper';
import Menu from '../Menu';
import Logo from './Logo';
import { HIDE_SMART_BANNER, SHOW_SMART_BANNER } from 'shared/constants';

const LeftNav = styled.div`
  display: flex;
  align-items: center;

  .hamburger {
    height: 48px;
    width: 48px;
    padding: 12px;
    border: none;
    background: transparent;
  }
`;

interface HeaderProps {
  newNavEnabled: boolean;
  pageType?: string;
}

const Index = (props: HeaderProps) => {
  const { newNavEnabled, pageType } = props;
  const hideHamburgerForHome = useSelector((state: State) =>
    getEligibleForVariant(state, AB_BOTTOM_NAV, 'B')
  );
  const shouldShowHamburger =
    pageType !== PageTypes.HOME || !hideHamburgerForHome;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hamburgerOnPDEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_PDP_HAMBURGER, 'A')
  );
  const isAddressBarEnabled = useIsAddressBarEnabled();
  const [hasLandedOnPD, setHasLandedOnPD] = useState(false);
  const isThemeHeader = useIsThemeHeader();
  // * The below key is needed to optimize the loading of menu component and API calls for Navigation
  const [isMenuMounted, setIsMenuMounted] = useState(false);
  const theme: Theme = useTheme();
  const { headerTextColor } = useIsConfigHeader();
  const fillColor = getFillColor(headerTextColor, isThemeHeader, theme);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setHasLandedOnPD(false);
    if (pageType === PageTypes.PRODUCT) {
      if (hasUTMParams()) {
        setHasLandedOnPD(true);
      }
    }
  }, [pageType]);

  const toggleMenu = () => {
    let body = document.getElementsByTagName('body')[0];

    if (!isMenuMounted) {
      pushEvent(HAMBURGER_CLICK, {
        interactionLocation: getInteractionLocation(pageType),
      });
      pushEvent(HIDE_SMART_BANNER);
      body.classList.add(BODY_OVERFLOW_FOR_MODAL);
      setIsMenuMounted(true);
      setIsMenuOpen(!isMenuOpen);
    } else {
      if (isMenuOpen) {
        body.classList.remove(BODY_OVERFLOW_FOR_MODAL);
        pushEvent(SHOW_SMART_BANNER);
      } else {
        pushEvent(HIDE_SMART_BANNER);
        body.classList.add(BODY_OVERFLOW_FOR_MODAL);
      }
      setIsMenuOpen(!isMenuOpen);
    }
  };

  return (
    <>
      <LeftNav color={theme.brandPrimary}>
        {!isAddressBarEnabled && (
          <>
            {!newNavEnabled && shouldShowHamburger && (
              <button
                ref={hamburgerRef}
                type="button"
                className="hamburger"
                aria-expanded={isMenuOpen}
                aria-label={'Open menu'}
                onClick={toggleMenu}
              >
                <HamburgerIcon
                  isLight={isThemeHeader}
                  color={headerTextColor}
                />
              </button>
            )}
            {newNavEnabled &&
              (hamburgerOnPDEnabled && hasLandedOnPD ? (
                <button
                  ref={hamburgerRef}
                  type="button"
                  className="hamburger"
                  aria-expanded={isMenuOpen}
                  aria-label={'Open menu'}
                  onClick={toggleMenu}
                >
                  <HamburgerIcon
                    isLight={isThemeHeader}
                    color={headerTextColor}
                  />
                </button>
              ) : (
                <BackNav
                  fillColor={fillColor}
                  isTransparentBG={isThemeHeader}
                  color={headerTextColor}
                />
              ))}
          </>
        )}
        <Logo shouldAddPadding={!shouldShowHamburger} />
      </LeftNav>
      {isMenuMounted && (
        <Menu
          hamburgerRef={hamburgerRef}
          isOpen={isMenuOpen}
          handleClose={toggleMenu}
        />
      )}
    </>
  );
};

export default Index;
